
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { LogOut, User } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

export const AuthButtons = ({
  isLoggedIn,
  onLoginClick,
  onSignUpClick,
}: {
  isLoggedIn: boolean;
  onLoginClick: () => void;
  onSignUpClick: () => void;
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSignOut = async () => {
    try {
      // Get current session state from Supabase
      const { data, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        // Just clear local state if we can't get the session
        queryClient.setQueryData(["session"], null);
        navigate('/');
        return;
      }

      // If we have a session, sign out
      if (data.session) {
        const { error } = await supabase.auth.signOut({
          scope: 'local' // Only clear local session
        });
        
        if (error) {
          console.error('Sign out error:', error);
        }
      }

      // Clear React Query cache after signout attempt
      queryClient.setQueryData(["session"], null);
      
      // Navigate before showing toast to prevent UI flicker
      navigate('/');
      
      toast({
        title: "Signed out",
        description: "You have been signed out of your account.",
      });
    } catch (error) {
      console.error('Sign out error:', error);
      queryClient.setQueryData(["session"], null);
      navigate('/');
      
      toast({
        title: "Error signing out",
        description: "There was a problem signing out, but you have been redirected to the home page.",
        variant: "destructive",
      });
    }
  };

  if (isLoggedIn) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/account">
          <Button 
            variant="ghost" 
            size="icon"
            title="My Account"
            className="h-9 w-9"
          >
            <User className="h-4 w-4" />
          </Button>
        </Link>
        <button 
          onClick={handleSignOut}
          title="Sign out"
          className="h-9 w-9 rounded-md flex items-center justify-center hover:bg-accent transition-colors duration-200 [&:not(:hover)]:bg-transparent outline-none focus:outline-none"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button variant="ghost" onClick={onLoginClick}>
        Sign In
      </Button>
      <Button onClick={onSignUpClick}>Sign Up</Button>
    </div>
  );
};
